import { get as _get } from 'lodash';
import {
    Commit,
    Dispatch,
} from 'vuex';

import { ExpandedEventInUrl } from '@app/enums';
import { IState } from '@app/store';
import StoreUtil from '@app/store/StoreUtil';
import OfferEvent from '@core/models/offer/OfferEvent';
import MediaService from '@core/services/media/MediaService';
import { VideoType } from '@core/services/media/enums';
import { isSportLicencedForVisualisation } from '@core/services/offer/visualisations';
import { VideoProviderType } from '@models/offer/enums';
import { BetGeniusRequest } from '@models/streams/BetGenius';
import { RelativeRootGetters } from '@store/types';
import { getEventId } from '@app/modules/eventDetailsPage/utils';

import {
    StreamState,
    StreamSection,
} from './enums';
import type {
    Getters,
    ISportPageState,
    VideoPlayerData,
    StreamDataFull,
    LastClickedEventInGrid,
} from './types';
import { getMatchStreams } from '../utils';
import {
    InPlaySortingFilter,
    OfferQuickFilter,
} from '@shared/timeFilter/offerQuickFilters';
import Vue from 'vue';

export { ISportPageState };

const mediaService = new MediaService();

const SET_DOCKED_STREAM = 'SET_DOCKED_STREAM';
const SET_FLOATING_STREAM = 'SET_FLOATING_STREAM';
const CLEAR_DOCKED_STREAM = 'CLEAR_DOCKED_STREAM';
const CLOSE_FLOATING_STREAM = 'CLOSE_FLOATING_STREAM';
const CHANGE_STREAM_STATE = 'CHANGE_STREAM_STATE';
const TOGGLE_DOCKED_VIDEO_SOUND = 'TOGGLE_DOCKED_VIDEO_SOUND';
const TOGGLE_FLOATING_VIDEO_SOUND = 'TOGGLE_FLOATING_VIDEO_SOUND';
const TOGGLE_EXPANDED_MARKET_ID = 'TOGGLE_EXPANDED_MARKET_ID';
const SET_EXPANDED_MARKET_IDS = 'SET_EXPANDED_MARKET_IDS';
const RESET_EXPANDED_MARKET_IDS = 'RESET_EXPANDED_MARKET_IDS';
const COLLAPSE_EXPANDED_MARKET_IDS = 'COLLAPSE_EXPANDED_MARKET_IDS';
const SET_SHOW_MORE_MARKET_LINES_MARKET_IDS = 'SET_SHOW_MORE_MARKET_LINES_MARKET_IDS';
const TOGGLE_STREAM_SECTION = 'TOGGLE_STREAM_SECTION';
const SET_STREAM_SECTION = 'SET_STREAM_SECTION';
const SET_LAST_CLICKED_EVENT_IN_GRID = 'SET_LAST_CLICKED_EVENT_IN_GRID';
const SET_ACTIVE_QUICK_FILTER = 'SET_ACTIVE_QUICK_FILTER';
const SET_ACTIVE_SORTING_FILTER = 'SET_ACTIVE_SORTING_FILTER';

const getters: Getters = {
    // TODO duplicate getter
    expandedEventData: (state: ISportPageState, getters: Getters, rootState: IState, rootGetters: RelativeRootGetters) => {
        return rootGetters['data/sportOffer/eventsMap'][getters.expandedEvent];
    },
    expandedEvent: (state: ISportPageState, getters: Getters) => {
        return getters.expandedEventRow.id;
    },
    expandedEventRow: (state: ISportPageState, getters: any, rootState: IState, rootGetters: any) => {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        if (rootGetters['navigation/route'].query[ExpandedEventInUrl.type]?.includes('stats')) {
            return { typeId: null, id: null };
        }

        let eventId = getEventId() || rootGetters['navigation/route'].query[ExpandedEventInUrl.event];
        eventId = eventId ? parseInt(eventId, 10) : null;

        if (!rootGetters['data/sportOffer/eventsMap'][eventId]) {
            return {
                typeId: null,
                id: null,
            };
        }

        const typeId = rootGetters['navigation/route'].query[ExpandedEventInUrl.type];
        return {
            typeId,
            id: eventId,
        };
    },
    expandedEventId: (state: ISportPageState, getters: any) => {
        return getters.expandedEventRow.id;
    },
    dockedVideoStream: StoreUtil.createSimpleGetter('dockedVideoStream'),
    floatingVideoStream: StoreUtil.createSimpleGetter('floatingVideoStream'),
    shouldDisplayDockedStream: (state: ISportPageState) => {
        return state.dockedVideoStream
            && _get(state, 'dockedVideoStream.matchId') === _get(state, 'floatingVideoStream.matchId');
    },
    selectedStreamSection: StoreUtil.createSimpleGetter('selectedStreamSection'),

    availableStreamSections(state: ISportPageState, getters: any, rootState: any, rootGetters: RelativeRootGetters) {
        const eventId = getters.expandedEventRow.id;
        if (!eventId) {
            return [];
        }

        const eventsMap = rootGetters['data/sportOffer/eventsMap'];
        const match = eventsMap[eventId];

        const sections = getMatchStreams(match);

        if (match.betRadarId && isSportLicencedForVisualisation(match.sportId)) {
            sections.push(StreamSection.Visualization);
        }

        return sections;
    },
    expandedMarketIds: StoreUtil.createSimpleGetter('expandedMarketIds'),
    showMoreMarketLinesMarketIds: StoreUtil.createSimpleGetter('showMoreMarketLinesMarketIds'),
    lastClickedEventInGrid: StoreUtil.createSimpleGetter('lastClickedEventInGrid'),
    activeQuickFilter: StoreUtil.createSimpleGetter('activeQuickFilter'),
    activeSortingFilter: StoreUtil.createSimpleGetter('activeSortingFilter'),
};

const store = {
    getters,
    state: {
        dockedVideoStream: null,
        floatingVideoStream: null,
        selectedStreamSection: null,
        expandedMarketIds: {},
        showMoreMarketLinesMarketIds: {},
        lastClickedEventInGrid: null,
        activeQuickFilter: OfferQuickFilter.Upcoming,
        activeSortingFilter: InPlaySortingFilter.Default,
    } as ISportPageState,

    mutations: {
        [SET_LAST_CLICKED_EVENT_IN_GRID]: (state: ISportPageState, event: LastClickedEventInGrid | null) => {
            state.lastClickedEventInGrid = event;
        },
        [SET_DOCKED_STREAM]: (state: ISportPageState, streamData: VideoPlayerData | StreamDataFull) => {
            state.dockedVideoStream = streamData;
        },
        [SET_FLOATING_STREAM]: (state: ISportPageState, streamData: VideoPlayerData | StreamDataFull) => {
            state.floatingVideoStream = streamData;
        },
        [CLEAR_DOCKED_STREAM]: (state: ISportPageState) => {
            state.dockedVideoStream = null;
        },
        [CLOSE_FLOATING_STREAM]: (state: ISportPageState) => {
            state.floatingVideoStream = null;
        },
        [CHANGE_STREAM_STATE]: (state: ISportPageState, streamState: StreamState) => {
            if (state.dockedVideoStream) {
                state.dockedVideoStream.state = streamState;
            }
        },
        [TOGGLE_DOCKED_VIDEO_SOUND]: StoreUtil.createToggleMutator('dockedVideoStream.isSoundOn'),
        [TOGGLE_FLOATING_VIDEO_SOUND]: StoreUtil.createToggleMutator('floatingVideoStream.isSoundOn'),
        [TOGGLE_STREAM_SECTION](state: ISportPageState, streamSection: StreamSection) {
            if (state.selectedStreamSection === streamSection) {
                state.selectedStreamSection = null;
            } else {
                state.selectedStreamSection = streamSection;
            }
        },
        [SET_STREAM_SECTION]: StoreUtil.createSimpleMutator('selectedStreamSection'),
        [TOGGLE_EXPANDED_MARKET_ID](state: ISportPageState, { groupId, marketId }: { groupId: number; marketId: string }) {
            if (state.expandedMarketIds[groupId][marketId]) {
                Vue.delete(state.expandedMarketIds[groupId], marketId);
            } else {
                Vue.set(state.expandedMarketIds[groupId], marketId, true);
            }
        },
        [SET_EXPANDED_MARKET_IDS](state: ISportPageState, { groupId, marketIds }: { groupId: number; marketIds: string[] }) {
            Vue.set(state.expandedMarketIds, groupId, marketIds);
        },
        [RESET_EXPANDED_MARKET_IDS](state: ISportPageState) {
            Vue.set(state, 'expandedMarketIds', {});
        },
        [COLLAPSE_EXPANDED_MARKET_IDS](state: ISportPageState, groupId: number) {
            Vue.set(state.expandedMarketIds, groupId, {});
        },
        [SET_SHOW_MORE_MARKET_LINES_MARKET_IDS]: StoreUtil.createSimpleMutator('showMoreMarketLinesMarketIds'),
        [SET_ACTIVE_QUICK_FILTER]: StoreUtil.createSimpleMutator('activeQuickFilter'),
        [SET_ACTIVE_SORTING_FILTER]: StoreUtil.createSimpleMutator('activeSortingFilter'),
    },

    actions: {
        setLastClickedEventInGrid: StoreUtil.createSimpleMutatorAction(SET_LAST_CLICKED_EVENT_IN_GRID),
        toggleSelectedStreamSection: StoreUtil.createSimpleMutatorAction(TOGGLE_STREAM_SECTION),
        setSelectedStreamSection: StoreUtil.createSimpleMutatorAction(SET_STREAM_SECTION),
        unpinVideo: ({ state, commit }: { state: ISportPageState; commit: Commit }) => {
            if (state.dockedVideoStream) {
                const dockedStreamState = { ...state.dockedVideoStream };
                commit(SET_FLOATING_STREAM, dockedStreamState);
            }
        },
        pinVideo: ({ commit, state }: { commit: Commit; state: ISportPageState }) => {
            if (!state.dockedVideoStream) {
                commit(SET_DOCKED_STREAM, state.floatingVideoStream);
            } else if (state.floatingVideoStream!.isSoundOn !== state.dockedVideoStream!.isSoundOn) {
                commit(TOGGLE_DOCKED_VIDEO_SOUND);
            }
            commit(CLOSE_FLOATING_STREAM);
        },
        closeDockedStream({ commit }: { commit: Commit }) {
            commit(CHANGE_STREAM_STATE, StreamState.Closed);
        },
        setIsVideoUnavailable({ commit }: { commit: Commit }) {
            commit(CHANGE_STREAM_STATE, StreamState.NotAvailable);
        },
        closeFloatingStream: StoreUtil.createSimpleMutatorAction(CLOSE_FLOATING_STREAM),
        clearDockedStream: StoreUtil.createSimpleMutatorAction(CLEAR_DOCKED_STREAM),
        playStream({ commit, state }: { commit: Commit; state: ISportPageState }) {
            const streamData = state.dockedVideoStream;
            commit(SET_DOCKED_STREAM, {
                ...streamData,
                state: StreamState.Playing,
            });
        },
        getPerform: StoreUtil.asyncErrorGuard((
            _context: { commit: Commit},
            { uuid, streamId, match }: { uuid: string; streamId: string, match: OfferEvent }) => {
            return mediaService.getPerform(streamId, uuid, match);
        }),
        getBetGenius: StoreUtil.asyncErrorGuard((
            _context: { commit: Commit },
            request: BetGeniusRequest, match: OfferEvent) => {
            return mediaService.getBetGenius(request, match);
        }),
        prepareStream: StoreUtil.asyncErrorGuard(async (
            { commit, state, rootGetters }:
                { commit: Commit; state: ISportPageState; dispatch: Dispatch; rootGetters: any },
            { match, streamSection }:
                { match: OfferEvent; streamSection?: StreamSection }) => {
            if (streamSection === undefined || streamSection === null || streamSection === StreamSection.Visualization) {
                return;
            }

            const videoPlayerData: VideoPlayerData = {
                matchId: match.id,
                state: StreamState.Loading,
                isSoundOn: state.dockedVideoStream ? state.dockedVideoStream.isSoundOn : true,
            };

            commit(SET_DOCKED_STREAM, videoPlayerData);

            try {
                let streamUrl;
                // this needs refactoring. these should be few different types (https://axilis.atlassian.net/browse/SW-3894
                let videoId;
                let videoType;
                let streamId;
                let uuid;
                let sourceId;

                const { userId } = rootGetters['data/authentication/user'];

                if (match.hasStream) {
                    if (streamSection && [StreamSection.Twitch, StreamSection.Youtube].includes(streamSection)) {
                        videoId = (match.streamInfo as Record<StreamSection, string | number>)[streamSection as (1 | 2 | 4 | 5)];
                        videoType = streamSection === StreamSection.Twitch ? VideoType.twitch : VideoType.youtube;
                    } else if (StreamSection.Perform === streamSection) {
                        videoType = VideoType.perform;
                        [uuid, streamId] = ((match.streamInfo as unknown) as Record<VideoProviderType, string>)[VideoProviderType.PERFORM]!.split(':');
                    } else if (StreamSection.BetGenius === streamSection) {
                        videoType = VideoType.betGenius;
                        [sourceId, videoId] = ((match.streamInfo as unknown) as Record<VideoProviderType, string>)[VideoProviderType.BET_GENIUS]!.split(':');
                    } else if (StreamSection.Beter === streamSection) {
                        videoType = VideoType.beter;
                        streamId = ((match.streamInfo as unknown) as Record<VideoProviderType, string>)[VideoProviderType.BETER];
                    } else {
                        const response = await mediaService.getMatchVideoStream(match, userId);

                        streamUrl = response?.streamUrl;
                        videoType = response?.videoType;
                    }
                }

                const streamData = {
                    ...videoPlayerData,
                    streamUrl,
                    videoId,
                    videoType,
                    uuid,
                    streamId,
                    sourceId,
                    state: StreamState.NotStarted,
                };

                commit(SET_DOCKED_STREAM, streamData);
            } catch (e) {
                commit(CHANGE_STREAM_STATE, StreamState.NotAvailable);
            }
        }),
        toggleDockedVideoSound: StoreUtil.createSimpleMutatorAction(TOGGLE_DOCKED_VIDEO_SOUND),
        toggleFloatingVideoSound: StoreUtil.createSimpleMutatorAction(TOGGLE_FLOATING_VIDEO_SOUND),
        toggleExpandedMarketId({ commit }: { commit: Commit }, { groupId, marketId }: { groupId: number; marketId: string }) {
            commit(TOGGLE_EXPANDED_MARKET_ID, { groupId, marketId });
        },
        setExpandedMarketIds({ commit }: { commit: Commit }, { groupId, marketIds }: { groupId: number; marketIds: Record<string, boolean> }) {
            commit(SET_EXPANDED_MARKET_IDS, { groupId, marketIds });
        },
        resetExpandedMarketIds({ commit }: { commit: Commit }) {
            commit(RESET_EXPANDED_MARKET_IDS);
        },
        collapseExpandedMarketIds({ commit }: { commit: Commit; }, groupId: number) {
            commit(COLLAPSE_EXPANDED_MARKET_IDS, groupId);
        },
        setShowMoreMarketLinesMarketIds({ commit }: { commit: Commit }, marketIds: { [key: number]: boolean }) {
            commit(SET_SHOW_MORE_MARKET_LINES_MARKET_IDS, marketIds);
        },
        setActiveQuickFilter: StoreUtil.createSimpleMutatorAction(SET_ACTIVE_QUICK_FILTER),
        setActiveSortingFilter: StoreUtil.createSimpleMutatorAction(SET_ACTIVE_SORTING_FILTER),
    },
};

export default store;
export type SportOfferStore = typeof store;
